/**
 * Inventory reports — tabbed view across 6 sub-reports:
 *   Overview · Reorder · Aging · Valuation · COGS vs Revenue · ABC
 *
 * Each tab is a self-contained panel that fetches its own data.
 * The active tab is also reflected in `?tab=` on the URL so it survives
 * email-report deep-links and refreshes.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  Boxes, AlertCircle, Loader2,
  LayoutGrid, ArrowDownToLine, Clock, IndianRupee, Layers, TrendingDown,
} from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { API } from '../../../lib/api';
import { formatErr } from '../../../components/admin/billing/shared';
import { ReportHeader, DateRange, StatCard, monthFirst, today } from './_shared';
import EmailReportButton from './EmailReportButton';
import { ReorderPanel, AgingPanel, ValuationPanel, CogsPanel, AbcPanel } from './InventoryPanels';

const CATEGORIES = [
  { val: '',          label: 'All categories',    color: 'bg-slate-100 text-slate-700' },
  { val: 'ppf',       label: 'PPF',               color: 'bg-emerald-100 text-emerald-700' },
  { val: 'sunfilm',   label: 'Sunfilm',           color: 'bg-amber-100 text-amber-700' },
  { val: 'bodyshop',  label: 'Body Shop',         color: 'bg-rose-100 text-rose-700' },
];

const TABS = [
  { key: 'overview',   label: 'Overview',     icon: LayoutGrid,       desc: 'Stock on hand · low-stock alerts · top consumers' },
  { key: 'reorder',    label: 'Reorder',      icon: ArrowDownToLine,  desc: 'Days of cover + suggested PO quantity' },
  { key: 'aging',      label: 'Aging',        icon: Clock,            desc: 'Dead stock buckets — 30 / 60 / 90+ days idle' },
  { key: 'valuation',  label: 'Valuation',    icon: IndianRupee,      desc: '₹ value on the shelf · per category' },
  { key: 'cogs',       label: 'COGS vs Revenue', icon: TrendingDown,  desc: 'Margin per category — material cost vs billings' },
  { key: 'abc',        label: 'ABC',          icon: Layers,           desc: 'Top movers (Class A) vs slow movers (Class C)' },
];

const OverviewPanel = ({ category, from, to, authHeader }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const params = new URLSearchParams();
      if (category) params.set('category', category);
      if (from) params.set('date_from', from);
      if (to) params.set('date_to', to);
      const { data: d } = await axios.get(`${API}/reports/inventory?${params}`, { headers: authHeader() });
      setData(d);
    } catch (e) { setErr(formatErr(e)); }
    finally { setLoading(false); }
  }, [authHeader, category, from, to]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="overview-panel">
      {err && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{err}</div>}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Items on file" value={data?.total_items ?? 0} testid="invrep-items" />
        <StatCard label="Total qty on hand" value={data?.total_qty_on_hand ?? 0} color="text-emerald-700" testid="invrep-onhand" />
        <StatCard label="Low-stock items" value={data?.low_stock_count ?? 0} color={data?.low_stock_count ? 'text-red-700' : 'text-slate-900'} testid="invrep-low" />
        <StatCard label="Consumed (period)" value={data?.consumed ?? 0} color="text-rose-700" hint={`Restocked: ${data?.restocked ?? 0}`} testid="invrep-consumed" />
      </div>
      {(data?.low_stock_items || []).length > 0 && (
        <div className="bg-white border border-red-200 rounded-xl overflow-hidden">
          <div className="px-4 py-2 border-b border-red-200 bg-red-50 flex items-center gap-2">
            <AlertCircle size={14} className="text-red-700" />
            <h2 className="text-sm font-semibold text-red-900">Low-stock items · {data.low_stock_items.length}</h2>
          </div>
          <table className="w-full text-xs">
            <thead className="bg-red-50/40 text-left border-b border-red-100">
              <tr>
                <th className="px-3 py-1.5 font-semibold text-slate-600">Item</th>
                <th className="px-3 py-1.5 font-semibold text-slate-600">Category</th>
                <th className="px-3 py-1.5 font-semibold text-slate-600 text-right">On hand</th>
                <th className="px-3 py-1.5 font-semibold text-slate-600 text-right">Safety stock</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-red-50">
              {data.low_stock_items.map((i) => (
                <tr key={i.id} className="hover:bg-red-50/30" data-testid={`invrep-low-row-${i.id}`}>
                  <td className="px-3 py-1.5 font-semibold text-slate-800">{i.name}</td>
                  <td className="px-3 py-1.5 text-slate-600"><span className="text-[10px] uppercase font-bold tracking-wider">{i.category}</span></td>
                  <td className="px-3 py-1.5 text-right font-mono text-red-700 font-bold">{i.quantity} {i.unit}</td>
                  <td className="px-3 py-1.5 text-right font-mono text-slate-500">{i.safety_stock} {i.unit}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-4 py-2 border-b border-slate-200 bg-slate-50">
          <h2 className="text-sm font-semibold text-slate-700">Top consumed items (in period)</h2>
        </div>
        {loading ? (
          <div className="p-6 text-center text-slate-500"><Loader2 className="inline animate-spin mr-2" size={14} /> Loading…</div>
        ) : (data?.top_consumers || []).length === 0 ? (
          <div className="p-6 text-center text-slate-400 text-sm">No consumption recorded in this period.</div>
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-slate-50/50 text-left border-b border-slate-100">
              <tr>
                <th className="px-3 py-1.5 font-semibold text-slate-600">Item</th>
                <th className="px-3 py-1.5 font-semibold text-slate-600">Category</th>
                <th className="px-3 py-1.5 font-semibold text-slate-600 text-right">Consumed</th>
                <th className="px-3 py-1.5 font-semibold text-slate-600 text-right">Jobs</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.top_consumers.map((i) => (
                <tr key={i.item_id} className="hover:bg-slate-50">
                  <td className="px-3 py-1.5 text-slate-800">{i.item_name}</td>
                  <td className="px-3 py-1.5 text-slate-600 uppercase text-[10px] tracking-wider">{i.category}</td>
                  <td className="px-3 py-1.5 text-right font-mono font-bold text-amber-700">{i.consumed}</td>
                  <td className="px-3 py-1.5 text-right font-mono">{i.entries}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-4 py-2 border-b border-slate-200 bg-slate-50">
          <h2 className="text-sm font-semibold text-slate-700">Full inventory ({data?.all_items?.length || 0})</h2>
        </div>
        <div className="overflow-x-auto max-h-[520px]">
          <table className="w-full text-xs">
            <thead className="bg-slate-50/70 sticky top-0 z-10 text-left border-b border-slate-100">
              <tr>
                <th className="px-3 py-1.5 font-semibold text-slate-600">Item</th>
                <th className="px-3 py-1.5 font-semibold text-slate-600">SKU</th>
                <th className="px-3 py-1.5 font-semibold text-slate-600">Category</th>
                <th className="px-3 py-1.5 font-semibold text-slate-600 text-right">Qty</th>
                <th className="px-3 py-1.5 font-semibold text-slate-600 text-right">Safety</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(data?.all_items || []).map((i) => (
                <tr key={i.id} className={i.low ? 'bg-red-50/30' : 'hover:bg-slate-50'}>
                  <td className="px-3 py-1.5 text-slate-800">{i.name}</td>
                  <td className="px-3 py-1.5 font-mono text-[10px] text-slate-500">{i.sku || '—'}</td>
                  <td className="px-3 py-1.5 text-slate-600 uppercase text-[10px] tracking-wider">{i.category}</td>
                  <td className={`px-3 py-1.5 text-right font-mono ${i.low ? 'text-red-700 font-bold' : ''}`}>{i.quantity} {i.unit}</td>
                  <td className="px-3 py-1.5 text-right font-mono text-slate-400">{i.safety_stock}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const InventoryReport = () => {
  const { authHeader } = useAuth();
  const [category, setCategory] = useState('');
  const [from, setFrom] = useState(monthFirst());
  const [to, setTo] = useState(today());
  const [tab, setTab] = useState(() => {
    try {
      const p = new URLSearchParams(window.location.search).get('tab');
      return TABS.find((t) => t.key === p) ? p : 'overview';
    } catch { return 'overview'; }
  });

  // Sync ?tab= so deep-links work from emails
  useEffect(() => {
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('tab', tab);
      window.history.replaceState({}, '', url.toString());
    } catch {}
  }, [tab]);

  const activeTabCfg = TABS.find((t) => t.key === tab);
  // The email-report payload mirrors the active tab so the inbox PDF
  // matches what the user is looking at.
  const emailReportType = (
    tab === 'overview' ? 'inventory'
    : tab === 'reorder' ? 'inventory-reorder'
    : tab === 'aging' ? 'inventory-aging'
    : tab === 'valuation' ? 'inventory-valuation'
    : tab === 'cogs' ? 'inventory-cogs'
    : tab === 'abc' ? 'inventory-abc'
    : 'inventory'
  );

  return (
    <div className="p-4 sm:p-6 space-y-4 bg-slate-50 min-h-full" data-testid="report-inventory">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <ReportHeader icon={Boxes} title="Inventory Reports" />
        <EmailReportButton
          reportType={emailReportType}
          getFilters={() => ({ category: category || undefined, date_from: from, date_to: to, tab })}
        />
      </div>

      {/* Tab strip */}
      <div className="bg-white border border-slate-200 rounded-xl p-1 flex flex-wrap gap-1 overflow-x-auto" data-testid="inv-tab-strip">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`flex-1 min-w-[120px] flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition ${active ? 'bg-teal-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100'}`}
              data-testid={`inv-tab-${t.key}`}
            >
              <Icon size={14} /> {t.label}
            </button>
          );
        })}
      </div>
      {activeTabCfg && (
        <p className="text-xs text-slate-500 -mt-2 pl-2">{activeTabCfg.desc}</p>
      )}

      {/* Category chips (shared across most tabs) */}
      {tab !== 'cogs' && (
        <div className="flex flex-wrap gap-2">
          {CATEGORIES.map((c) => (
            <button
              key={c.val || 'all'}
              onClick={() => setCategory(c.val)}
              className={`text-xs font-semibold px-3 py-1.5 rounded-full transition border ${category === c.val ? `${c.color} border-current shadow-sm` : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-50'}`}
              data-testid={`invrep-chip-${c.val || 'all'}`}
            >
              {c.label}
            </button>
          ))}
        </div>
      )}

      {/* Date range (only relevant for Overview + COGS) */}
      {(tab === 'overview' || tab === 'cogs') && (
        <DateRange from={from} to={to} onFrom={setFrom} onTo={setTo} onApply={() => { /* tabs reload reactively */ }} />
      )}

      {/* Active tab content */}
      {tab === 'overview'  && <OverviewPanel  category={category} from={from} to={to} authHeader={authHeader} />}
      {tab === 'reorder'   && <ReorderPanel   category={category}                         authHeader={authHeader} />}
      {tab === 'aging'     && <AgingPanel     category={category}                         authHeader={authHeader} />}
      {tab === 'valuation' && <ValuationPanel category={category}                         authHeader={authHeader} />}
      {tab === 'cogs'      && <CogsPanel                          from={from} to={to}     authHeader={authHeader} />}
      {tab === 'abc'       && <AbcPanel       category={category}                         authHeader={authHeader} />}
    </div>
  );
};

export default InventoryReport;
